import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Quill } from 'react-quill';
import { SketchPicker } from 'react-color';
import styled from 'styled-components';

const PickerWrapper = styled.div`
  position: absolute;
  z-index: 999;
  left: 0;
  top: 40px;
  pointer-events: auto;

  input {
    color: #000000;
  }
`;

type Props = {
  quill: Quill;
  isVisible: boolean;
};

function EditorBgColorPicker({ quill, isVisible }: Props) {
  const [selection, setSelection] = useState<any>(null);
  const [isPickerFocused, setPickerFocused] = useState(false);
  const [selectColor, setColor] = useState('#ffffff');
  const scrollPositionRef = useRef(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const windowScrollRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (!quill || !quill.root) return;
    const container = quill.root.closest('.ql-container') as HTMLElement | null;
    scrollContainerRef.current =
      (container?.parentElement as HTMLElement) || null;
  }, [quill]);

  useEffect(() => {
    if (!quill) return;
    const handleSelectionChange = (range: any) => {
      if (range && !isPickerFocused) setSelection(range);
    };
    quill.on('selection-change', handleSelectionChange);
    return () => {
      quill.off('selection-change', handleSelectionChange);
    };
  }, [quill, isPickerFocused]);

  const handlePickerFocus = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (scrollContainerRef.current) {
        scrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }
      windowScrollRef.current = { x: window.scrollX, y: window.scrollY };
      if (quill) quill.blur();
      setPickerFocused(true);
      if (quill) (quill as any).__suppressScrollIntoView = true;
    },
    [quill],
  );

  const handlePickerBlur = useCallback(() => {
    setPickerFocused(false);

    if (quill && selection) {
      setTimeout(() => {
        const sel: any = (quill as any).selection;
        const originalScrollIntoView = sel && sel.scrollIntoView;
        if (sel && typeof sel.scrollIntoView === 'function') {
          sel.scrollIntoView = () => {};
        }
        quill.setSelection(selection.index, selection.length);

        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        const { x, y } = windowScrollRef.current;
        if (typeof x === 'number' && typeof y === 'number') {
          window.scrollTo(x, y);
        }
        setTimeout(() => {
          (quill as any).__suppressScrollIntoView = Boolean(
            isVisible && isPickerFocused,
          );
          if (sel) sel.scrollIntoView = originalScrollIntoView;
        }, 16);
      }, 0);
    }
  }, [quill, selection, isVisible, isPickerFocused]);

  const handleColorSelection = useCallback(
    (color) => {
      if (!quill) return;
      const hexColor = color.hex;
      const range = quill.getSelection();

      if (selection || range) {
        (quill as any).__suppressScrollIntoView = true;
        const idx = selection?.index ?? range?.index ?? 0;
        const len = selection?.length ?? range?.length ?? 0;
        try {
          quill.formatText(idx, len, 'background', hexColor, 'silent');
        } catch (_) {
          quill.formatText(idx, len, 'background', hexColor);
        }
      }

      setColor(hexColor);
      handlePickerBlur();
    },
    [quill, selection, handlePickerBlur],
  );

  return (
    <PickerWrapper
      onMouseEnter={handlePickerFocus}
      onMouseLeave={handlePickerBlur}
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <SketchPicker
        color={selectColor}
        onChange={handleColorSelection}
        disableAlpha={true}
      />
    </PickerWrapper>
  );
}

export default EditorBgColorPicker;
